import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { buildResourceTableInsertContent } from '../lib/post_block_indent.js'
import { syncResourceRowParagraphsForResource } from '../lib/sync_tiptap_resource_row_titles.js'
import {
  afterResourceRowInsertFocus,
  paragraphRowRole,
  resolveResourceTableRowInsert,
  resourceRowTitleText,
  RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
} from '../lib/resource_row_paragraph.js'

export const DownloadExtension = Node.create({
  name: 'download',

  group: 'block',

  content: 'downloadRow+',

  defining: true,

  addAttributes() {
    return {
      href: { default: null },
      filename: { default: null },
      contentType: { default: null },
      signedId: { default: null },
    }
  },

  parseHTML() {
    return []
  },

  renderHTML() {
    // This extension no longer renders containers
    return ['div']
  },

  addCommands() {
    return {
      setDownload:
        (options) =>
        ({ commands, editor, state }) => {
          const title = options && (options.title || options.filename) ? (options.title || options.filename) : 'Click to edit title';
          const ok = commands.insertContent(
            buildResourceTableInsertContent(state, [
              {
                type: 'downloadRow',
                attrs: options,
                content: [
                  {
                    type: 'paragraph',
                    attrs: RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
                    content: [{ type: 'text', text: title }],
                  },
                ],
              },
            ])
          );
          if (ok && editor) {
            afterResourceRowInsertFocus(editor, state, {
              resourceId: options?.resourceId,
              rowType: 'downloadRow',
            });
          }
          return ok;
        },

      addDownloadRow:
        (options) =>
        ({ commands, editor, state }) => {
          const insertCtx = resolveResourceTableRowInsert(state);
          const title = options && (options.title || options.filename) ? (options.title || options.filename) : 'Click to edit title';
          const ok = commands.insertContent({
            type: 'downloadRow',
            attrs: options,
            content: [
              {
                type: 'paragraph',
                attrs: RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
                content: [{ type: 'text', text: title }],
              },
            ],
          });
          if (ok && editor) {
            afterResourceRowInsertFocus(editor, state, {
              resourceId: options?.resourceId,
              rowType: 'downloadRow',
              insertCtx,
            });
          }
          return ok;
        },
    }
  },
})

// Debounce utility for resource title updates
const createDebouncedResourceUpdate = () => {
  const timeouts = new Map(); // resourceId -> timeout

  return (resourceId, newTitle) => {
    // Don't send update if resourceId is missing or title is empty/whitespace only
    if (!resourceId) return;
    
    // If title is empty, clear any pending timeout and don't send update
    if (!newTitle || typeof newTitle !== 'string' || newTitle.trim().length === 0) {
      if (timeouts.has(resourceId)) {
        clearTimeout(timeouts.get(resourceId));
        timeouts.delete(resourceId);
      }
      return;
    }

    // Clear existing timeout for this resource
    if (timeouts.has(resourceId)) {
      clearTimeout(timeouts.get(resourceId));
    }

    // Set new timeout with longer delay to prevent updates during rapid deletion
    // If user is deleting quickly, the timeout will be cleared before it fires
    const timeout = setTimeout(async () => {
      timeouts.delete(resourceId);
      
      const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '';
      
      try {
        const trimmedTitle = newTitle.trim();
        // Double-check title is not empty before sending
        if (trimmedTitle.length === 0) {
          return;
        }
        
        const response = await fetch(`/admin/resources/${resourceId}.json`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({
            resource: {
              title: trimmedTitle
            }
          })
        });

        if (!response.ok) {
          console.warn('Failed to update resource title:', await response.text());
          return;
        }
        syncResourceRowParagraphsForResource('downloadRow', resourceId, trimmedTitle);
      } catch (error) {
        console.error('Error updating resource title:', error);
      }
    }, 1500); // Increased from 500ms to 1500ms to prevent updates during rapid deletion

    timeouts.set(resourceId, timeout);
  };
};

const debouncedResourceUpdate = createDebouncedResourceUpdate();

export const DownloadRowExtension = Node.create({
  name: 'downloadRow',

  group: 'block',

  content: 'paragraph+',

  defining: true,

  addAttributes() {
    return {
      href: {
        default: null,
      },
      filename: {
        default: null,
      },
      resourceId: {
        default: null,
      },
      contentType: {
        default: null,
      },
      signedId: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'tr[data-resource-row]',
        priority: 100, // High priority to parse first
        getAttrs: element => {
          // Extract href from various possible attributes
          const href = element.getAttribute('data-href') || 
                      element.getAttribute('href') || 
                      null;
          const resourceId = element.getAttribute('data-resource-id') || null;
          // Check if this is a download row by looking for download icon
          const isDownload =
            element.querySelector('img[src*="download.svg"]') ||
            element.querySelector('.icon-ps-download');
          return isDownload ? { href, resourceId, rowType: 'download' } : false;
        },
        contentElement: element => {
          // Find the text cell (second td)
          const textCell = element.querySelector('td:last-child');
          return textCell;
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { href, filename, contentType, signedId, resourceId } = HTMLAttributes;

    const elementAttrs = mergeAttributes(HTMLAttributes, {
      'data-resource-row': '',
      'data-row-type': 'download',
      'data-disposition': 'attachment',
    });

    if (signedId) {
      elementAttrs['data-blob-signed-id'] = signedId;
      elementAttrs['data-blob-attrs'] = 'href,data-href';
    }

    // Attach metadata as data-attributes so frontend upload handler can read them
    if (filename) elementAttrs['data-file-name'] = filename;
    if (contentType) elementAttrs['data-content-type'] = contentType;
    if (signedId) elementAttrs['data-signed-id'] = signedId;
    if (resourceId) elementAttrs['data-resource-id'] = resourceId.toString();

    // Normalize URL if provided
    const formattedUrl = href && href !== 'null' ? href : null;

    if (formattedUrl) {
      elementAttrs['data-href'] = formattedUrl;
      elementAttrs['href'] = formattedUrl;
      elementAttrs.style = 'cursor: pointer;';
      elementAttrs.onclick = `
        const tr = event.currentTarget || event.target.closest('tr[data-resource-row]');
        if (!tr) return;
        const editor = window.currentTiptapEditor;
        if (!editor || !editor.isEditable) {
          const targetUrl = tr.dataset.href || tr.getAttribute('href');
          if (targetUrl) {
            window.open(targetUrl, '_blank');
          }
        }
        // If editable, do nothing on single click
      `;
    }

    return [
      'tr',
      elementAttrs,
      [
        'td',
        {
          colspan: '1',
          rowspan: '1'
        },
        [
          'span',
          {
            class: 'ps-resource-row__icon',
            draggable: 'false',
            contenteditable: 'false',
            'data-resource-row-icon': 'download',
          },
          ['span', { class: 'icon-ps-download', 'aria-hidden': 'true' }],
        ],
      ],
      [
        'td',
        {
          colspan: '1',
          rowspan: '1',
          class: 'download-title font-primary-bold',
        },
        0
      ]
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('downloadRowKeyupHandler'),
        props: {
          handleDOMEvents: {
            keyup: (view, event) => {
              const { state } = view;
              const { selection } = state;
              const { $from } = selection;

              // Find if we're inside a downloadRow
              for (let depth = $from.depth; depth >= 0; depth--) {
                const node = $from.node(depth);
                if (node.type.name === 'downloadRow') {
                  const resourceId = node.attrs.resourceId;
                  if (resourceId && paragraphRowRole($from.parent) !== 'subtitle') {
                    const newTitle = resourceRowTitleText(node);
                    if (newTitle.length > 0) {
                      debouncedResourceUpdate(resourceId, newTitle);
                    } else {
                      debouncedResourceUpdate(resourceId, '');
                    }
                  }
                  break;
                }
              }
              return false;
            },
          },
        },
      }),
    ];
  },

  addCommands() {
    return {}
  },

})
